import React, { useState } from 'react'
import { Button } from '../../../components/shadcn/Button'
import { FormInput } from '../../../components/Form/FormInput'
import Modal from '../../../components/Modal'
import { Controller, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { ControlledTextarea } from '../../../components/Form/Controlled/ControlledTextarea'
import { toastPromise } from '../../../lib/toastHelpers'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/pro-duotone-svg-icons'
import { useGQLMutation } from '../../../hooks/GQLUtilityHooks'
import { FragmentType, graphql, useFragment } from '../../../gql'
import { order, OrderItem } from '../../../gql/graphql'
import { faBan, faCheck, faTrash } from '@fortawesome/pro-solid-svg-icons'

const updateOrderItemMutation = graphql(`
  mutation UpdateOrderItem($id: Int!, $note: String!, $quantity: Int!) {
    updateOrderItem(id: $id, note: $note, quantity: $quantity) {
      data {
        ...EditOrderItemModal_OrderItemFragment
        ...OrderItemsTable_OrderItemFragment
      }
      errors
    }
  }
`)

const deleteOrderItemMutation = graphql(`
  mutation DeleteOrderItem($id: Int!) {
    deleteOrderItem(id: $id) {
      errors
    }
  }
`)

const EditOrderItemModal_OrderItemFragment = graphql(`
  fragment EditOrderItemModal_OrderItemFragment on OrderItem {
    id
    name
    note
    quantity
    order {
      id
    }
  }
`)

export const EditOrderItemModal: React.FC<{
  item: FragmentType<typeof EditOrderItemModal_OrderItemFragment>
}> = props => {
  const orderItem = useFragment(EditOrderItemModal_OrderItemFragment, props.item)

  const [isOpen, setIsOpen] = useState(false)
  const [note, setNote] = useState(orderItem.note)

  const {
    handleSubmit,
    control,
    formState: { isDirty },
    reset,
  } = useForm({
    defaultValues: {
      quantity: orderItem.quantity,
      note: orderItem.note,
    },
  })

  const queryClient = useQueryClient()

  const {
    mutateAsync: orderUpdateMutate,
    isPending: isLoading,
    error,
  } = useGQLMutation(updateOrderItemMutation, ['updateOrderItem'], {
    onSuccess: dataFragment => {
      const data = useFragment(EditOrderItemModal_OrderItemFragment, dataFragment)
      if (data?.id) {
        queryClient.setQueryData(['order', { id: orderItem.order.id }], (old: { order: order }) => {
          if (old) {
            return {
              order: {
                ...old.order,
                data: {
                  ...old.order.data,
                  items: old.order.data.items?.map((item: OrderItem) =>
                    item.id === orderItem.id
                      ? {
                          ...item,
                          ...data,
                        }
                      : item,
                  ),
                },
              },
            }
          }
        })
        closeModal()
        reset({
          quantity: data?.quantity,
          note: data?.note,
        })
      }
    },
  })

  const { mutateAsync: deleteMutate, isPending: deleteIsLoading } = useGQLMutation(
    deleteOrderItemMutation,
    ['deleteOrderItem'],
    {
      onSuccess: () => {
        queryClient.setQueryData(['order', { id: orderItem.order.id }], (old: { order: order }) => {
          return {
            order: {
              ...old.order,
              data: {
                ...old.order.data,
                items: old.order.data.items?.filter((item: OrderItem) => item.id !== orderItem.id) || [],
              },
            },
          }
        })
        closeModal()
      },
    },
  )

  const handleSave = handleSubmit(data => {
    toastPromise(orderUpdateMutate({ id: orderItem.id, ...data }), {
      pending: 'Ukládám objednávku...',
      success: 'Objednávka uložena',
      error: 'Chyba při ukládání objednávky',
    }).catch(e => console.error(e))
  })

  const handleDelete = () => {
    toastPromise(deleteMutate({ id: orderItem.id }), {
      pending: 'Smazání položky...',
      success: 'Položka smazána',
      error: 'Chyba při smazání položky',
    }).catch(e => console.error(e))
  }

  const closeModal = () => setIsOpen(false)

  const openModal = () => {
    setIsOpen(true)
  }

  return (
    <>
      <div
        className="bg-primary-blue rounded-xl px-3 py-2 text-white flex items-center gap-2 text-md cursor-pointer select-none"
        onClick={openModal}>
        <FontAwesomeIcon icon={faPen} size="lg" className="text-white" />
        Upravit
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} title={`Upravit položku ${orderItem.name}`}>
        <div className="flex flex-col mt-4">
          <h2 className="text-sm font-bold mb-1">Počet kusů</h2>
          <div className={'flex items-stretch gap-2 '}>
            <Controller
              control={control}
              name="quantity"
              render={({ field: { onChange, value } }) => (
                <>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    title={'Odebrat 10'}
                    onClick={() => onChange(value > 9 ? value - 10 : 0)}>
                    {'-10'}
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    title={'Odebrat 1'}
                    onClick={() => onChange(value > 0 ? value - 1 : 0)}>
                    {'-1'}
                  </Button>
                  <FormInput
                    inputclassName={'text-right'}
                    className={'w-28 flex-1'}
                    type={'number'}
                    value={`${value}`}
                    onChange={q => onChange(Number(q) >= 0 ? Number(q) : 0)}
                  />
                  <Button variant="secondary" className="flex-1" title={'Přidat 1'} onClick={() => onChange(value + 1)}>
                    {'+1'}
                  </Button>
                  <Button
                    variant="secondary"
                    className="flex-1"
                    title={'Přidat 10'}
                    onClick={() => onChange(value + 10)}>
                    {'+10'}
                  </Button>
                </>
              )}
            />
          </div>

          <ControlledTextarea
            control={control}
            name="note"
            label={'Poznámka k položce'}
            className={'mt-2'}
            value={note}
            onChange={e => setNote(e.target.value)}
          />

          {error && <div className="mt-2 text-red-500">{error}</div>}

          <div className="mt-4 flex justify-between gap-2">
            <div className={'flex gap-2'}>
              <Button
                variant="destructive"
                icon={faTrash}
                title={'Smazat položku'}
                onClick={handleDelete}
                disabled={deleteIsLoading}>
                {'Smazat položku'}
              </Button>
            </div>
            <div className={'flex gap-2'}>
              <Button variant="outline" icon={faBan} title={'Zrušit'} onClick={closeModal} disabled={isLoading}>
                {'Zrušit'}
              </Button>
              <Button
                variant="default"
                icon={faCheck}
                title={'Uložit'}
                onClick={handleSave}
                disabled={isLoading || !isDirty || deleteIsLoading}>
                {'Uložit'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  )
}
